import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { LineReveal, Reveal } from "@/components/LineReveal";
import { Marquee } from "@/components/Marquee";
import { ProductCard } from "@/components/ProductCard";
import { EmailCapture } from "@/components/EmailCapture";
import { Footer } from "@/components/Footer";

const HERO_IMG =
  "https://images.unsplash.com/photo-1587923131674-53edaa1553e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxibGFjayUyMGFuZCUyMHdoaXRlJTIwcGVvbnl8ZW58MHx8fHwxNzg3NDIyMTQ4fDA&ixlib=rb-4.1.0&q=85";
const LOOK_1 = "/designs/look-white.webp";
const LOOK_2 = "/designs/look-black.webp";
const LOOK_3 = "/designs/look-duo.webp";

const CHAPTERS = [
  {
    num: "01",
    title: "Origin",
    body: "Paeon — παιώνια — the peony. A flower the Greeks tied to healing and permanence. We build clothing the same way: made to be kept, not consumed.",
  },
  {
    num: "02",
    title: "Fabric",
    body: "100% cotton. Heavyweight, structured, unapologetic. Every graphic is DTG printed with archival precision — detail you feel before you see it.",
  },
  {
    num: "03",
    title: "The City",
    body: "Los Angeles rooftops. Museum marble. Hollywood after dark. San Diego salt air. San Francisco fog. PAEON is cut for the cities that never sit still.",
  },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] bg-[#050505] text-white overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <motion.img
          src={HERO_IMG}
          alt="Black and white peony"
          initial={{ scale: 1.18, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.5 }}
          transition={{ duration: 2.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full h-full object-cover grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 pb-20 md:pb-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="text-[10px] md:text-xs tracking-[0.4em] uppercase text-white/60 mb-6"
          data-testid="hero-kicker"
        >
          Premium Streetwear — Est. Los Angeles — ΠΑΙΩΝΙΑ
        </motion.p>
        <h1 className="font-serif uppercase leading-[0.85] tracking-tight" data-testid="hero-headline">
          <LineReveal
            immediate
            delay={0.25}
            lines={["Wear the", "City."]}
            lineClassName="text-[17vw] md:text-[13vw]"
          />
        </h1>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.9 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mt-10"
        >
          <p className="max-w-sm text-sm text-white/60 leading-relaxed">
            Heavyweight 100% cotton. DTG printed. Minimalist to the eye, detailed to the touch — modern and ancient at once.
          </p>
          <Link
            to="/shop"
            data-testid="hero-shop-button"
            className="group inline-flex items-center gap-4 border border-white/40 px-8 py-4 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-white hover:text-black transition-colors duration-300 w-fit"
          >
            Enter the Shop
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
};

const Manifesto = () => (
  <section id="manifesto" data-testid="manifesto-section" className="bg-white text-black py-32 md:py-48 px-6 md:px-12">
    <LineReveal
      lines={["The Manifesto"]}
      className="font-serif text-5xl md:text-7xl tracking-tight mb-24 md:mb-36"
    />
    <div className="space-y-0">
      {CHAPTERS.map((c, i) => (
        <motion.div
          key={c.num}
          data-testid={`manifesto-chapter-${c.num}`}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className={`grid md:grid-cols-12 gap-6 py-16 md:py-20 border-t border-black/10 ${i % 2 === 1 ? "md:text-right" : ""}`}
        >
          <div className={`md:col-span-2 ${i % 2 === 1 ? "md:order-3" : ""}`}>
            <span className="font-serif italic text-6xl md:text-7xl text-black/15">{c.num}</span>
          </div>
          <div className="md:col-span-3">
            <h3 className="text-xs tracking-[0.35em] uppercase font-semibold">{c.title}</h3>
          </div>
          <div className="md:col-span-7">
            <p className="font-serif text-2xl md:text-4xl leading-snug text-black/85">{c.body}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);

const Spotlight = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <section ref={ref} data-testid="spotlight-section" className="bg-[#050505] text-white py-32 md:py-48 px-6 md:px-12 overflow-hidden">
      <div className="grid md:grid-cols-12 gap-10 items-center">
        <motion.div style={{ y }} className="md:col-span-6">
          <Reveal clip>
            <img
              src="/designs/plate-9.webp"
              alt="Lucky Number Seven — PAEON 006 back print"
              loading="lazy"
              className="w-full aspect-[4/5] object-cover"
            />
          </Reveal>
        </motion.div>
        <div className="md:col-span-5 md:col-start-8">
          <p className="text-[10px] tracking-[0.35em] uppercase text-white/40 mb-8">Spotlight — 006</p>
          <LineReveal
            lines={["Lucky", "Number", "Seven."]}
            className="font-serif text-6xl md:text-8xl leading-[0.95] tracking-tight mb-10"
          />
          <p className="text-sm text-white/60 leading-relaxed max-w-sm mb-4">
            The laurel, the seven, the stack. Our most-wanted back print — white ink on heavyweight black cotton, DTG pressed.
          </p>
          <p className="text-sm text-white/60 leading-relaxed max-w-sm mb-10">$70 — 100% cotton, 7oz heavyweight.</p>
          <Link
            to="/shop/paeon_006"
            data-testid="spotlight-shop-link"
            className="group inline-flex items-center gap-4 border border-white/40 px-8 py-4 text-xs tracking-[0.3em] uppercase font-semibold hover:bg-white hover:text-black transition-colors duration-300 w-fit"
          >
            Shop the Seven
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const Lookbook = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const y2 = useTransform(scrollYProgress, [0, 1], [120, -40]);
  const y3 = useTransform(scrollYProgress, [0, 1], [80, -30]);

  return (
    <section ref={ref} data-testid="lookbook-section" className="bg-[#F9F9F9] text-black py-32 md:py-48 px-6 md:px-12 overflow-hidden">
      <div className="grid md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-5">
          <LineReveal
            lines={["Worn, not", "styled."]}
            className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight mb-10"
          />
          <p className="text-sm text-black/50 leading-relaxed max-w-sm">
            Shot in the streets it was made for. No studio, no set — just heavyweight cotton against concrete and sky.
          </p>
        </div>
        <motion.div style={{ y: y1 }} className="md:col-span-4 md:mt-24">
          <div className="overflow-hidden">
            <img src={LOOK_1} alt="PAEON street look" loading="lazy" className="w-full aspect-[3/4] object-cover img-spotlight" />
          </div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-black/40 mt-3">Look 01 — Downtown</p>
        </motion.div>
        <motion.div style={{ y: y2 }} className="md:col-span-3 md:mt-48">
          <div className="overflow-hidden">
            <img src={LOOK_2} alt="PAEON editorial look" loading="lazy" className="w-full aspect-[3/4] object-cover img-spotlight" />
          </div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-black/40 mt-3">Look 02 — Rooftop</p>
        </motion.div>
      </div>
      <motion.div style={{ y: y3 }} className="mt-16 md:mt-24 md:w-3/4 md:mx-auto">
        <div className="overflow-hidden">
          <img src={LOOK_3} alt="PAEON — black and white, side by side" loading="lazy" className="w-full aspect-[4/5] md:aspect-[5/4] object-cover img-spotlight" />
        </div>
        <p className="text-[10px] tracking-[0.3em] uppercase text-black/40 mt-3 flex justify-between">
          <span>Look 03 — After Dark</span>
          <span>Black × White</span>
        </p>
      </motion.div>
    </section>
  );
};

const PLATES = [
  { src: "/designs/paeon-script.webp", num: "I", title: "The Signature", dark: false },
  { src: "/designs/plate-5.webp", num: "II", title: "The Wordmark", dark: false },
  { src: "/designs/plate-9.webp", num: "III", title: "Lucky Number Seven", dark: true },
  { src: "/designs/plate-6.webp", num: "IV", title: "The Dove", dark: true },
  { src: "/designs/plate-7.webp", num: "V", title: "The Mosaic", dark: true },
  { src: "/designs/plate-8.webp", num: "VI", title: "The Laurel", dark: false },
  { src: "/designs/plate-3.webp", num: "VII", title: "Word Search", dark: false },
  { src: "/designs/plate-4.webp", num: "VIII", title: "The Goddess", dark: false },
];

const Signature = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section ref={ref} data-testid="signature-section" className="bg-white text-black py-32 md:py-48 px-6 md:px-12 border-t border-black/10">
      <div className="grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5">
          <p className="text-[10px] tracking-[0.35em] uppercase text-black/40 mb-8">The Design — Plate I</p>
          <LineReveal
            lines={["Hand-scripted.", "Machine-sharp."]}
            className="font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight mb-10"
          />
          <p className="text-sm text-black/55 leading-relaxed max-w-sm">
            Every PAEON graphic begins as calligraphy, then meets the grid. This is the signature back print — old hand, new city, DTG printed with archival inks on heavyweight cotton.
          </p>
        </div>
        <motion.div style={{ y }} className="md:col-span-6 md:col-start-7">
          <Reveal clip className="border border-black/15 bg-white p-6 md:p-10">
            <img
              src="/designs/paeon-script.webp"
              alt="PAEON Clothing — Premium Streetwear signature design"
              loading="lazy"
              className="w-full object-contain"
            />
          </Reveal>
          <p className="text-[10px] tracking-[0.3em] uppercase text-black/40 mt-4 flex justify-between">
            <span>PAEON Clothing — Premium Streetwear</span>
            <span>Plate I / VIII</span>
          </p>
        </motion.div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mt-24 md:mt-32">
        {PLATES.map((p, i) => (
          <Reveal
            key={p.num}
            clip
            delay={i * 0.12}
            data-testid={`plate-${p.num}`}
          >
            <div className={`border border-black/15 aspect-[3/4] overflow-hidden ${p.dark ? "bg-[#050505]" : "bg-white"}`}>
              <img src={p.src} alt={`${p.title} — Plate ${p.num}`} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <p className="flex justify-between text-[9px] tracking-[0.3em] uppercase text-black/40 mt-3">
              <span>{p.title}</span>
              <span>Plate {p.num}</span>
            </p>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Featured = ({ products }) => (
  <section data-testid="featured-section" className="bg-white text-black py-32 md:py-44 px-6 md:px-12">
    <div className="flex items-end justify-between mb-16 md:mb-24">
      <LineReveal lines={["Drop 001"]} className="font-serif text-5xl md:text-7xl tracking-tight" />
      <Link
        to="/shop"
        data-testid="featured-view-all-link"
        className="hidden md:inline-flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase font-semibold border-b border-black pb-1 hover:opacity-50 transition-opacity"
      >
        View All <ArrowRight size={12} />
      </Link>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
      {products.map((p, i) => (
        <ProductCard key={p.lookup_key} product={p} index={i} />
      ))}
    </div>
    <div className="mt-14 md:hidden">
      <Link
        to="/shop"
        data-testid="featured-view-all-link-mobile"
        className="inline-flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase font-semibold border-b border-black pb-1"
      >
        View All <ArrowRight size={12} />
      </Link>
    </div>
  </section>
);

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <Featured products={products} />
      <Marquee dark />
      <Manifesto />
      <Signature />
      <Spotlight />
      <Lookbook />
      <EmailCapture dark />
      <Footer />
    </main>
  );
}
